import { FeatureTypes } from '@/constants';
import configurationAPI from '@/api/configuration';

const GLIA_SCRIPT_ID = 'glia-integration-script';

export default {
	namespaced: true,
	state: {
		smartyStreetsToken: '',
		isGliaAttached: false,
		isGliaToggleEnabled: false,
		isGliaToggleLoaded: false
	},
	getters: {
		isGliaAttached: state => state.isGliaAttached,
		isGliaToggleEnabled: state => state.isGliaToggleEnabled,
		isGliaToggleLoaded: state => state.isGliaToggleLoaded,
		smartyStreetsToken: state => state.smartyStreetsToken
	},
	mutations: {
		setData(state, { objName, data }) {
			state[objName] = data;
		},
		resetState(state) {
			state.smartyStreetsToken = '';
			state.isGliaAttached = false;
			state.isGliaToggleEnabled = false;
			state.isGliaToggleLoaded = false;
		}
	},
	actions: {
		async loadGliaToggle({ commit, state }) {
			// Only load if not already loaded
			if (state.isGliaToggleLoaded) {
				return state.isGliaToggleEnabled;
			}

			try {
				const isGliaEnabled = await configurationAPI.getFeatureToggleStatus(FeatureTypes.Glia);
				commit('setData', { objName: 'isGliaToggleEnabled', data: isGliaEnabled });
				commit('setData', { objName: 'isGliaToggleLoaded', data: true });
				return isGliaEnabled;
			} catch (error) {
				commit('setData', { objName: 'isGliaToggleEnabled', data: false });
				commit('setData', { objName: 'isGliaToggleLoaded', data: true });
				return false;
			}
		},
		async attachGliaScript({ commit, state, dispatch }) {
			try {
				// Don't re-attach if already attached
				if (state.isGliaAttached) {
					return;
				}

				// Load Glia toggle status if not already loaded
				const isGliaEnabled = await dispatch('loadGliaToggle');

				if (!isGliaEnabled) {
					return;
				}

				const existingScript = typeof document !== 'undefined' ? document.getElementById(GLIA_SCRIPT_ID) : null;
				if (existingScript) {
					commit('setData', { objName: 'isGliaAttached', data: true });
					return;
				}

				// Create and attach Glia script
				const gliaIntegrationScriptUrl = 'https://api.glia.com/salemove_integration.js';
				const scriptElement = document.createElement('script');

				scriptElement.id = GLIA_SCRIPT_ID;
				scriptElement.async = true;
				scriptElement.src = gliaIntegrationScriptUrl;
				scriptElement.type = 'text/javascript';

				scriptElement.addEventListener('load', function () {
					// Update Glia with OLA-specific product identification
					if (window.sm) {
						window.sm.getApi({ version: 'v1' }).then(function (glia) {
							glia.updateInformation({
								customAttributes: {
									product: 'OLA'
								}
							});

							// Block file sharing (APP-20543)
							glia.addEventListener(glia.EVENTS.ENGAGEMENT_START, (engagement) => {
								try {
									if (engagement && 'allowFileSending' in engagement) {
										engagement.allowFileSending = false;
									}
								} catch (error) {
									console.error('Failed to disable file sharing in Glia engagement:', error);
								}
							});
						});
					}
					commit('setData', { objName: 'isGliaAttached', data: true });
				});

				scriptElement.addEventListener('error', function () {
					commit('setData', { objName: 'isGliaAttached', data: false });
				});

				document.body.append(scriptElement);
			} catch (error) {
				commit('setData', { objName: 'isGliaAttached', data: false });
			}
		},
		// Keep the original attachGlia action for backward compatibility
		async attachGlia({ dispatch }) {
			return dispatch('attachGliaScript');
		},
		async detachGliaScript({ commit }) {
			try {
				if (typeof document !== 'undefined') {
					const existingScript = document.getElementById(GLIA_SCRIPT_ID);
					if (existingScript && existingScript.parentNode) {
						existingScript.parentNode.removeChild(existingScript);
					}
				}

				if (typeof window !== 'undefined' && window.sm && typeof window.sm.getApi === 'function') {
					window.sm
						.getApi({ version: 'v1' })
						.then(glia => {
							if (glia) {
								if (typeof glia.destroy === 'function') {
									glia.destroy();
								} else if (typeof glia.unload === 'function') {
									glia.unload();
								}
							}
						})
						.catch(() => {
							// Ignore errors when Glia API is unavailable during teardown
						});
				}
			} finally {
				commit('setData', { objName: 'isGliaAttached', data: false });
			}
		}
	}
};
